import { XMarkIcon } from "@heroicons/react/24/solid"
import { Dispatch, SetStateAction, Suspense, useCallback, useState } from "react"
import { twMerge } from "tailwind-merge"
import { Blockgroup } from "db"
import { ReportFinder as ReportFinderUI } from "ui"
import { OpenSearch } from "./components/open-search"
import { ReportLink } from "./components/report-link"
import { ReportType } from "./types"
import { getRelatedReportOptionsByReportType } from "./utils"
import { FilteredSearch } from "./components/filtered-search"
import { REPORT_FINDER_SEARCHTABS } from "./constants"
import Help from "components/help"
import { useTranslation } from "react-i18next"
import { translateCongressionalDistrictName } from "app/utils/formatting_utils"

const NationalHeader = ({ onReportFinderClose }: { onReportFinderClose: () => void }) => {
  const { t, i18n } = useTranslation(["location-insights"])
  const isSpanish = i18n.language === "es"
  return (
    <>
      <div className="flex w-full flex-row items-baseline justify-between pb-2">
        <p
          className={twMerge(
            "max-w-[70%] text-lg font-semibold text-brand-green-dark",
            isSpanish ? "leading-4" : ""
          )}
        >
          {t("location-insights:finder.location_prompt")}
        </p>
        <Help className="-ml-4">
          <p>{t("location-insights:finder.intro")}</p>
          <p className="mt-2">{t("location-insights:finder.scale_info")}</p>
        </Help>
        <button
          className="flex items-center text-gray-400 hover:text-black"
          onClick={onReportFinderClose}
        >
          <XMarkIcon className="-mt-2 h-4 w-4 fill-brand-green-dark transition-colors duration-100 hover:fill-brand-green" />
        </button>
      </div>
      <p className="w-full pb-2 text-sm font-semibold text-gray-700">
        {t("location-insights:finder.map_selection")}
      </p>
    </>
  )
}

const ReportsHeader = ({ onReportFinderClose }: { onReportFinderClose: () => void }) => {
  const { t } = useTranslation(["location-insights"])
  return (
    <div className="flex w-full flex-row justify-between">
      <p className="pb-2 pt-1 text-sm font-semibold text-gray-700">
        {t("location-insights:finder.related_geographies")}
      </p>
      <button
        className="flex items-center text-gray-400 hover:text-black"
        onClick={onReportFinderClose}
      >
        <XMarkIcon className="-mt-2 h-4 w-4 fill-brand-green-dark transition-colors duration-100 hover:fill-brand-green" />
      </button>
    </div>
  )
}
export const ReportFinder = ({
  setOpen,
  blockgroups,
  reportType,
  isNational,
}: {
  setOpen: Dispatch<SetStateAction<boolean>>
  blockgroups: Blockgroup[]
  reportType?: ReportType
  isNational?: boolean
}) => {
  const { t } = useTranslation(["location-insights", "common"])
  const translatedSearchTabs = REPORT_FINDER_SEARCHTABS.map((tab) => ({
    ...tab,
    title: t(tab.title),
  }))
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const relatedReportOptions = blockgroups
    ? getRelatedReportOptionsByReportType(blockgroups, reportType).map((option) => ({
        ...option,
        name:
          option.type === "common:district"
            ? translateCongressionalDistrictName(option.name, t)
            : option.name,
        type: t(option.type as string),
      }))
    : []

  const onReportFinderClose = useCallback(() => setOpen(false), [setOpen])

  return (
    <ReportFinderUI.Root
      className={twMerge("absolute right-0 top-10 z-10", !searchModalOpen && "p-2")}
    >
      {!searchModalOpen && (
        <>
          {isNational ? (
            <NationalHeader onReportFinderClose={onReportFinderClose} />
          ) : (
            <ReportsHeader onReportFinderClose={onReportFinderClose} />
          )}
          <ReportFinderUI.ReportOptionsMenu
            reportOptions={relatedReportOptions}
            fallbackText={t("location-insights:finder.map_instructions")}
            ReportLink={ReportLink}
            hideOptionsCategory={isNational}
          />
          <p className="mb-2 w-full border-t pt-1 text-xs font-semibold text-gray-700 lg:block">
            {t("location-insights:finder.looking_elsewhere")}
          </p>
        </>
      )}
      <ReportFinderUI.ReportSearchModal
        open={searchModalOpen}
        setOpen={setSearchModalOpen}
        tabs={translatedSearchTabs}
        onReportFinderClose={onReportFinderClose}
        triggerText={t("location-insights:finder.search_all")}
      >
        <ReportFinderUI.ModalTab id={REPORT_FINDER_SEARCHTABS[0].id}>
          <OpenSearch />
        </ReportFinderUI.ModalTab>
        <ReportFinderUI.ModalTab id={REPORT_FINDER_SEARCHTABS[1].id}>
          <Suspense fallback={null}>
            <FilteredSearch />
          </Suspense>
        </ReportFinderUI.ModalTab>
      </ReportFinderUI.ReportSearchModal>
    </ReportFinderUI.Root>
  )
}
